/**
 * Toasts, and the undo affordance that replaces confirmation dialogs.
 *
 * House rule: a reversible action happens immediately and offers "Ångra". Only the truly
 * irreversible gets a dialog. Asking "är du säker?" for everything trains people to click yes.
 */
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

export type ToastTone = "info" | "pass" | "warn" | "fail";

export type ToastInput = {
  message: string;
  tone?: ToastTone;
  /** Providing this turns the toast into the undo affordance. */
  undo?: () => void;
  durationMs?: number;
};

type Toast = ToastInput & { id: number };

const DEFAULT_DURATION_MS = 5000;
/** Undo needs long enough to read the message, look away, and change your mind. */
const UNDO_DURATION_MS = 8000;

const ToastContext = createContext<((toast: ToastInput) => void) | null>(null);

export function useToast(): (toast: ToastInput) => void {
  const show = useContext(ToastContext);
  if (!show) {
    throw new Error("useToast must be used inside <ToastProvider>");
  }
  return show;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback((toast: ToastInput) => {
    nextId.current += 1;
    setToasts((current) => [...current, { ...toast, id: nextId.current }]);
  }, []);

  return (
    <ToastContext.Provider value={show}>
      {children}
      <div
        // Polite: a toast reports what already happened, it never interrupts.
        aria-live="polite"
        aria-label="Meddelanden"
        className="pointer-events-none fixed bottom-s6 right-s6 z-50 grid w-[min(380px,calc(100vw-32px))] gap-s4"
      >
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const TONE_CLASS: Record<ToastTone, string> = {
  info: "border-l-drop",
  pass: "border-l-pass-text",
  warn: "border-l-warn-text",
  fail: "border-l-fail-text",
};

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: number) => void }) {
  const [paused, setPaused] = useState(false);
  const duration = toast.durationMs ?? (toast.undo ? UNDO_DURATION_MS : DEFAULT_DURATION_MS);

  useEffect(() => {
    if (paused) {
      return;
    }
    const timer = setTimeout(() => onDismiss(toast.id), duration);
    return () => clearTimeout(timer);
  }, [paused, duration, toast.id, onDismiss]);

  return (
    <div
      role={toast.tone === "fail" ? "alert" : "status"}
      // Hovering or tabbing in stops the clock, so "Ångra" cannot disappear under the cursor.
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className={`nb-animate-panel pointer-events-auto flex items-center gap-s5 border-l-[3px] ${
        TONE_CLASS[toast.tone ?? "info"]
      } rounded-card border border-line bg-surface-1 px-s5 py-s4 shadow-dialog`}
    >
      <p className="flex-1 text-body text-ink">{toast.message}</p>
      {toast.undo && (
        <button
          type="button"
          className="nb-btn nb-btn--ghost shrink-0"
          onClick={() => {
            toast.undo?.();
            onDismiss(toast.id);
          }}
        >
          Ångra
        </button>
      )}
      <button
        type="button"
        aria-label="Stäng meddelande"
        className="nb-btn nb-btn--ghost shrink-0 px-s3"
        onClick={() => onDismiss(toast.id)}
      >
        ✕
      </button>
    </div>
  );
}
